import React, { useCallback, useEffect, useRef } from 'react';
import { bool, func, number, object, shape, string } from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { get } from 'ravenjs/utils/lodash';

import { actions as authActions } from 'modules/auth';
import { actions as pendoActions } from 'modules/pendo';
import { actions as uiActions } from 'modules/ui';
import { EVENTS } from 'constants/events';
import { MODALS } from 'constants/modals';
import { selectors as userSelectors } from 'modules/user';
import { isPublicRoute } from 'utils/thrservice';
import { APP_PERMISSIONS } from 'constants/permissions';
import { ROOT_PRIVATE_ROUTE, ROOT_PRIVATE_ROUTE_PLATFORM_LITE } from 'constants/routes';
import Acl from 'modules/acl';
import USERS from 'constants/users';

import AccountSection from './AccountSection';
import AccountSwitcherHeaderStyled, {
    AccountSwitcherHeaderInner,
    SectionSlot,
} from './AccountSwitcherHeaderStyled';
import AccountSwitcherList from './AccountSwitcherList';
import ImpersonationSection from './ImpersonationSection';

const AccountSwitcherHeader = ({
    authorized,
    history,
    userCompany,
    openModal,
    closeModal,
    isImpersonatingUser,
    user,
    switchCompany,
    sendPendoEvent,
    partnerId,
    userRole,
    isMCA,
}) => {
    const pathname = get(get(history, 'location', {}), 'pathname', '');
    const partnerName = get(userCompany, 'companyName', '');
    const isPrimaryAccount = get(userCompany, 'isPrimaryCompany', false);
    const hasSentBannerEvent = useRef(false);
    const prevIsPrimaryAccountRef = useRef(isPrimaryAccount);
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (authorized && !isPublicRoute(pathname) && !hasSentBannerEvent.current) {
            sendPendoEvent({
                ...EVENTS.ACCOUNT_SWITCHER.VIEWING_ACCOUNT_BANNER_VISIBLE,
                eventProperties: {
                    ...EVENTS.ACCOUNT_SWITCHER.VIEWING_ACCOUNT_BANNER_VISIBLE.eventProperties,
                    is_primary_account: isPrimaryAccount,
                    partner_account_id: partnerId,
                    user_role: userRole,
                },
            });
            hasSentBannerEvent.current = true;
        }
    }, [authorized, pathname, isPrimaryAccount, partnerId, userRole, sendPendoEvent]);

    useEffect(() => {
        hasSentBannerEvent.current = false;
    }, [isPrimaryAccount]);

    // Track view mode changes and fire event when isPrimaryAccount actually changes
    useEffect(() => {
        // Skip the first render - we only want to track actual changes
        if (isFirstRender.current) {
            isFirstRender.current = false;
            prevIsPrimaryAccountRef.current = isPrimaryAccount;
            return;
        }

        // Only fire event if isPrimaryAccount actually changed
        if (prevIsPrimaryAccountRef.current !== isPrimaryAccount) {
            sendPendoEvent({
                ...EVENTS.ACCOUNT_SWITCHER.VIEW_MODE_CHANGED,
                eventProperties: {
                    ...EVENTS.ACCOUNT_SWITCHER.VIEW_MODE_CHANGED.eventProperties,
                    previous_view_mode: prevIsPrimaryAccountRef.current ? 'primary' : 'non_primary',
                    current_view_mode: isPrimaryAccount ? 'primary' : 'non_primary',
                    partner_account_id: partnerId,
                    user_role: userRole,
                },
            });
            prevIsPrimaryAccountRef.current = isPrimaryAccount;
        }
    }, [isPrimaryAccount, partnerId, userRole, sendPendoEvent]);

    const handleSwitchAccountConfirm = useCallback(
        async selectedCompanyId => {
            if (!selectedCompanyId) {
                return;
            }

            sendPendoEvent({
                ...EVENTS.ACCOUNT_SWITCHER.SWITCH_ACCOUNT_MODAL_CONFIRMED,
                eventProperties: {
                    ...EVENTS.ACCOUNT_SWITCHER.SWITCH_ACCOUNT_MODAL_CONFIRMED.eventProperties,
                    from_partner_account_id: partnerId,
                    to_partner_account_id: selectedCompanyId,
                    is_primary_account: isPrimaryAccount,
                    user_role: userRole,
                },
            });

            closeModal(MODALS.COMMON);

            await switchCompany(selectedCompanyId);

            // view_mode_changed event is now fired via useEffect when isPrimaryAccount changes
            // This ensures accurate previous/current view mode tracking based on actual state

            if (Acl.check(APP_PERMISSIONS.platformliteCompanyDashboardView)) {
                history.push(ROOT_PRIVATE_ROUTE_PLATFORM_LITE);
            } else {
                history.push(ROOT_PRIVATE_ROUTE);
            }
        },
        [closeModal, switchCompany, history, sendPendoEvent, partnerId, isPrimaryAccount, userRole]
    );

    const handleCloseModal = useCallback(() => {
        closeModal(MODALS.COMMON);
    }, [closeModal]);

    const handleButtonClick = useCallback(() => {
        sendPendoEvent({
            ...EVENTS.ACCOUNT_SWITCHER.SWITCH_ACCOUNT_BUTTON_CLICKED,
            eventProperties: {
                ...EVENTS.ACCOUNT_SWITCHER.SWITCH_ACCOUNT_BUTTON_CLICKED.eventProperties,
                user_role: userRole,
                partner_account_id: partnerId,
                is_primary_account: isPrimaryAccount,
            },
        });
    }, [sendPendoEvent, userRole, partnerId, isPrimaryAccount]);

    const handleSwitchAccount = useCallback(() => {
        handleButtonClick();

        openModal(MODALS.COMMON, {
            hasCloseIcon: true,
            isComponent: true,
            hideActions: true,
            hideIcon: true,
            description: (
                <AccountSwitcherList
                    onCancel={handleCloseModal}
                    onConfirm={handleSwitchAccountConfirm}
                    currentPartnerName={partnerName}
                    isMCA={isMCA}
                />
            ),
            ModalProps: {
                ContentProps: {
                    style: {
                        maxWidth: '872px',
                        margin: '20px',
                        borderRadius: '8px',
                    },
                },
            },
            modalProps: {
                headerProps: {
                    display: 'none',
                },
                bodyProps: {
                    padding: '12px',
                },
            },
        });
    }, [
        openModal,
        handleCloseModal,
        handleSwitchAccountConfirm,
        partnerName,
        handleButtonClick,
        isMCA,
    ]);

    const handleEndImpersonation = useCallback(() => {
        openModal(MODALS.CONFIRM, {
            title: USERS.IMPERSONATION_MODAL.TITLE,
            description: USERS.IMPERSONATION_MODAL.DESCRIPTION,
            actions: [
                {
                    text: USERS.IMPERSONATION_MODAL.BUTTON.CANCEL,
                    color: 'secondary',
                    onClick() {
                        closeModal(MODALS.CONFIRM);
                    },
                },
                {
                    text: USERS.IMPERSONATION_MODAL.BUTTON.OK,
                    color: 'primary',
                    onClick() {
                        closeModal(MODALS.CONFIRM);
                        setTimeout(() => {
                            history.push('/impersonation/end');
                        }, 300);
                    },
                },
            ],
        });
    }, [openModal, closeModal, history]);

    if (isPublicRoute(pathname) || !authorized) {
        return null;
    }

    return (
        <AccountSwitcherHeaderStyled className="accountSwitcherBar">
            <AccountSwitcherHeaderInner>
                <SectionSlot
                    half={isImpersonatingUser}
                    withDivider={isImpersonatingUser}
                    stackedDivider={isImpersonatingUser}
                >
                    <AccountSection
                        partnerName={partnerName}
                        isPrimaryAccount={isPrimaryAccount}
                        onSwitchAccount={handleSwitchAccount}
                    />
                </SectionSlot>
                {isImpersonatingUser && (
                    <SectionSlot half>
                        <ImpersonationSection
                            impersonatedName={get(user, 'fullName', '')}
                            onEndSession={handleEndImpersonation}
                        />
                    </SectionSlot>
                )}
            </AccountSwitcherHeaderInner>
        </AccountSwitcherHeaderStyled>
    );
};

AccountSwitcherHeader.propTypes = {
    authorized: bool,
    history: shape({
        location: shape({
            pathname: string,
        }),
    }),
    userCompany: object,
    openModal: func.isRequired,
    closeModal: func.isRequired,
    isImpersonatingUser: bool,
    user: object,
    switchCompany: func.isRequired,
    sendPendoEvent: func.isRequired,
    partnerId: number,
    userRole: string,
    isMCA: bool,
};

AccountSwitcherHeader.defaultProps = {
    authorized: false,
    userCompany: {},
    history: {},
    isImpersonatingUser: false,
    user: {},
    partnerId: null,
    userRole: '',
    isMCA: false,
};

const mapStateToProps = createStructuredSelector({
    userCompany: userSelectors.getUserCompany,
    partnerId: userSelectors.getPartnerId,
    userRole: userSelectors.getUserRole,
});

const mapDispatchToProps = {
    openModal: uiActions.openModal,
    closeModal: uiActions.closeModal,
    switchCompany: authActions.switchCompany.trigger,
    sendPendoEvent: pendoActions.sendPendoEvent.trigger,
};

export { AccountSwitcherHeader as UnwrappedAccountSwitcherHeader };
export default connect(mapStateToProps, mapDispatchToProps)(AccountSwitcherHeader);
